import { forwardRef } from 'react'
import { cn } from '../../utils/cn'
import { useTheme } from '../providers/ThemeProvider'

// ═══════════════════════════════════════════════════════════════════════════
// Card - Journal Entry Style for Diary Theme
// ═══════════════════════════════════════════════════════════════════════════

interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Card variant for diary theme */
  variant?: 'default' | 'journal' | 'note' | 'sticky'
}

// Helper to filter out background/color classes when in diary mode
const filterDiaryClasses = (className: string | undefined, isDiary: boolean): string => {
  if (!isDiary || !className) return className || ''
  
  // Remove bg-*, from-*, to-*, via-*, text-* (color) classes in diary mode
  // so our diary styles take precedence
  return className
    .split(' ')
    .filter(cls => {
      if (cls.startsWith('bg-')) return false
      if (cls.startsWith('from-')) return false
      if (cls.startsWith('to-')) return false
      if (cls.startsWith('via-')) return false
      if (cls.match(/^text-(gray|blue|green|red|purple|pink|orange|yellow|indigo|emerald|rose|slate|white|black)/)) return false
      if (cls.startsWith('border-0')) return false
      if (/dark:(bg|text|border|ring|from|to|via|divide|placeholder)-/.test(cls)) return false
      return true
    })
    .join(' ')
}

const Card = forwardRef<HTMLDivElement, CardProps>(
  ({ className, variant = 'default', style, ...props }, ref) => {
    const { isDiary, yanamasCardStyle } = useTheme()
    
    // Diary theme styles based on variant — GEOMETRY only, colour from tokens
    const getDiaryStyles = (): React.CSSProperties => {
      // 2026-08-29 UI audit: every colour below used to be a hardcoded
      // light-cream / sepia rgba. Inline styles beat the diary token bridge in
      // theme.css, so on html.diary-dark the card painted itself near-white and
      // then wore the dark theme's light ink — unreadable. Colour comes from the
      // semantic tokens now (they already resolve to paper & ink in BOTH diary
      // themes); only the journal GEOMETRY stays hardcoded here.
      const baseStyles: React.CSSProperties = {
        backgroundColor: 'var(--bg-surface)',
        color: 'var(--text-primary)',
        border: '1px solid var(--border-soft)',
        borderLeft: '4px solid var(--text-secondary)', // Sepia left border - THICK and visible
        borderRadius: '4px',
        boxShadow: '3px 3px 10px var(--shadow-color)', // Softer shadow
        backdropFilter: 'blur(2px)', // Slight blur for depth
      }

      // The DEFAULT card's look is a user choice (yanamasCardStyle, default
      // 'index' per the theme-lab picks). journal/note/sticky keep their
      // distinct semantic looks below.
      const styledDefault = (): React.CSSProperties => {
        switch (yanamasCardStyle) {
          case 'index':
            // Index card: red margin rule down the left (C-CARD 3)
            return {
              ...baseStyles,
              backgroundColor: 'var(--bg-surface)',
              borderLeft: '1px solid var(--border-soft)',
              borderRadius: '3px',
              backgroundImage:
                'linear-gradient(90deg, transparent 26px, color-mix(in srgb, var(--bookmark-red) 40%, transparent) 26px, color-mix(in srgb, var(--bookmark-red) 40%, transparent) 27px, transparent 27px)',
            }
          case 'ruled':
            return {
              ...baseStyles,
              backgroundColor: 'var(--bg-surface)',
              borderLeft: '3px solid var(--bookmark-red)',
              borderRadius: '2px',
              backgroundImage:
                'repeating-linear-gradient(transparent 0 26px, var(--paper-margin) 26px 27px)',
            }
          case 'taped':
            // Tape strip drawn by .yn-card-taped::before (yanamas-theme.css)
            return {
              ...baseStyles,
              backgroundColor: 'var(--bg-surface)',
              borderLeft: '1px solid var(--border-soft)',
              borderRadius: '2px',
              overflow: 'visible',
            }
          case 'folded':
            // Dog-ear drawn by .yn-card-folded::after (yanamas-theme.css)
            return {
              ...baseStyles,
              backgroundColor: 'var(--bg-surface)',
              borderLeft: '1px solid var(--border-soft)',
              position: 'relative',
              overflow: 'hidden',
            }
          case 'plain':
          default:
            return baseStyles
        }
      }
      
      switch (variant) {
        case 'journal':
          return {
            ...baseStyles,
            backgroundColor: 'var(--bg-surface)',
            borderLeft: '5px solid var(--text-secondary)',
            boxShadow: '4px 4px 12px var(--shadow-color)',
          }
        case 'note':
          return {
            ...baseStyles,
            backgroundColor: 'var(--bg-surface-alt)', // Aged paper
            borderLeft: '5px solid var(--bookmark-red)', // Bookmark red
            boxShadow: '4px 4px 15px var(--shadow-color)',
            transform: 'rotate(-0.5deg)',
          }
        case 'sticky':
          return {
            backgroundColor: 'var(--sticky-note)', // Yellow sticky — themed (night palette is dark)
            color: 'var(--text-primary)',
            border: 'none',
            borderLeft: 'none',
            boxShadow: '4px 4px 12px var(--shadow-color)',
            transform: 'rotate(1deg)',
          }
        default:
          return styledDefault()
      }
    }
    
    // Filter className in diary mode to remove conflicting background/color classes
    const filteredClassName = filterDiaryClasses(className, isDiary)
    
    return (
      <div
        ref={ref}
        className={cn(
          'card rounded-card shadow-sm',
          !isDiary && 'border bg-surface border-soft text-primary',
          isDiary && 'diary-card diary-hover-lift', // Added hover lift animation
          isDiary && variant === 'default' && yanamasCardStyle === 'taped' && 'yn-card-taped',
          isDiary && variant === 'default' && yanamasCardStyle === 'folded' && 'yn-card-folded',
          filteredClassName
        )}
        style={isDiary ? { ...getDiaryStyles(), ...style } : style}
        data-testid="card"
        {...props}
      />
    )
  }
)
Card.displayName = 'Card'

const CardHeader = forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, style, ...props }, ref) => {
  const { isDiary } = useTheme()
  
  return (
    <div
      ref={ref}
      className={cn('flex flex-col space-y-1.5 p-6', className)}
      style={isDiary ? { 
        borderBottom: '1px dashed var(--paper-margin)',
        ...style 
      } : style}
      {...props}
    />
  )
})
CardHeader.displayName = 'CardHeader'

const CardTitle = forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, style, ...props }, ref) => {
  const { isDiary } = useTheme()
  const filteredClassName = filterDiaryClasses(className, isDiary)

  return (
    <h3
      ref={ref}
      className={cn('text-2xl font-semibold leading-none tracking-tight', filteredClassName)}
      style={isDiary ? { 
        // Track the theme var so the user's handwriting pick applies here
        fontFamily: "var(--font-handwritten, 'Gochi Hand', cursive)",
        color: 'var(--ink-sepia)', // Sepia color - explicit
        fontSize: '1.5rem',
        fontWeight: 400, // Handwritten fonts look better lighter
        ...style 
      } : style}
      {...props}
    />
  )
})
CardTitle.displayName = 'CardTitle'

const CardDescription = forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, style, ...props }, ref) => {
  const { isDiary } = useTheme()
  const filteredClassName = filterDiaryClasses(className, isDiary)
  
  return (
    <p
      ref={ref}
      className={cn('text-sm', !isDiary && 'text-secondary', filteredClassName)}
      style={isDiary ? { 
        color: 'var(--ink-faded)',
        fontFamily: 'var(--font-body)',
        ...style 
      } : style}
      {...props}
    />
  )
})
CardDescription.displayName = 'CardDescription'

const CardContent = forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, style, ...props }, ref) => {
  const { isDiary } = useTheme()
  const filteredClassName = filterDiaryClasses(className, isDiary)
  
  return (
    <div
      ref={ref}
      className={cn('p-6 pt-0', filteredClassName)}
      style={isDiary ? { 
        fontFamily: 'var(--font-body)',
        color: 'var(--ink-black)',
        ...style 
      } : style}
      {...props}
    />
  )
})
CardContent.displayName = 'CardContent'

const CardFooter = forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, style, ...props }, ref) => {
  const { isDiary } = useTheme()
  
  return (
    <div
      ref={ref}
      className={cn('flex items-center p-6 pt-0', className)}
      style={isDiary ? { 
        borderTop: '1px dashed var(--paper-margin)',
        marginTop: '1rem',
        paddingTop: '1rem',
        ...style 
      } : style}
      {...props}
    />
  )
})
CardFooter.displayName = 'CardFooter'

export { Card, CardHeader, CardFooter, CardTitle, CardDescription, CardContent }